import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ProgressPeriod } from '@/lib/api-client';

/**
 * First calendar year the product had activity - floors the past-year history
 * so we never offer (or accept from a URL) a year that predates any data.
 */
export const HISTORY_FLOOR_YEAR = 2024;

const PERIOD_PARAM: Record<string, ProgressPeriod> = {
  month: 'this_month',
  year: 'this_year',
};
const PARAM_FOR_PERIOD: Partial<Record<ProgressPeriod, string>> = {
  this_month: 'month',
  this_year: 'year',
};

function readYear(raw: string | null): number | undefined {
  if (!raw || !/^\d{4}$/.test(raw)) return undefined;
  const year = Number(raw);
  const current = new Date().getFullYear();
  return year >= HISTORY_FLOOR_YEAR && year < current ? year : undefined;
}

/**
 * Which window the Progress page is showing, kept in the URL.
 *
 * `?period=month|year` (absent = this week) and `?year=2025` for a past-year
 * archive. It used to be component state, so a reload, or coming back to the
 * page from a lesson, always reset to "This week" - on a phone, where the page
 * is left and re-entered constantly, that was most visits.
 *
 * Every change REPLACES the entry rather than pushing one: the period is a
 * filter on one page, not a place, so the back button leaves Progress instead
 * of stepping back through each period the user glanced at. Other params are
 * kept. A value the page cannot show (`?period=decade`, `?year=1999`, a year
 * that has not ended) reads as the default rather than as an error.
 */
export function useProgressView(): {
  period: ProgressPeriod;
  pastYear: number | undefined;
  setPeriod: (period: ProgressPeriod) => void;
  setPastYear: (year: number | undefined) => void;
} {
  const [params, setParams] = useSearchParams();
  const period = PERIOD_PARAM[params.get('period') ?? ''] ?? 'this_week';
  const pastYear = readYear(params.get('year'));

  const update = useCallback(
    (key: string, value: string | undefined) =>
      setParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (value) next.set(key, value);
          else next.delete(key);
          return next;
        },
        { replace: true }
      ),
    [setParams]
  );

  const setPeriod = useCallback(
    (next: ProgressPeriod) => update('period', PARAM_FOR_PERIOD[next]),
    [update]
  );
  const setPastYear = useCallback(
    (year: number | undefined) => update('year', year ? String(year) : ''),
    [update]
  );

  return { period, pastYear, setPeriod, setPastYear };
}
